import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { unsubscribeByCategories } from '../../auth/_requests';
import './UnsubscribeEmails.css';

type ScreenState = 'selecting' | 'loading' | 'success' | 'error' | 'invalid_token';

const CATEGORIES = [
  {
    id: 'marketing',
    label: 'Marketing emails',
    description: 'Promotions, announcements, newsletters, and special offers.',
    icon: '📣',
  },
  {
    id: 'transactional_non_mandatory',
    label: 'Transactional emails',
    description: 'Account activity updates, reminders, tips, and non-critical notices.',
    icon: '🔔',
  },
] as const;

type CategoryId = (typeof CATEGORIES)[number]['id'];


const UnsubscribeEmails = () => {
  const { token: pathToken } = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();

  const token = pathToken ?? searchParams.get('token') ?? null;

  const [screen, setScreen] = useState<ScreenState>(token ? 'selecting' : 'invalid_token');
  const [selected, setSelected] = useState<Set<CategoryId>>(new Set());
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setScreen('invalid_token');
    }
  }, [token]);

  const toggleCategory = (id: CategoryId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const noneSelected = selected.size === 0;

  const handleSubmit = async () => {
    if (!token || noneSelected) return;
    setScreen('loading');
    try {
      await unsubscribeByCategories(token, Array.from(selected));
      setScreen('success');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setScreen('error');
    }
  };

  return (
    <div className='unsubscribe-page'>
      <div className='unsubscribe-bg' />
      <motion.div
        className='unsubscribe-card'
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
      >
        {/* Logo / Brand */}
        <div className='unsubscribe-brand'>
          <span className='unsubscribe-brand-dot' />
          TenantPay
        </div>

        {/* ─── SELECTING STATE ─── */}
        {screen === 'selecting' && (
          <>
            <div className='unsubscribe-header'>
              <div className='unsubscribe-icon-wrap unsubscribe-icon-neutral'>
                <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' width='30' height='30'>
                  <path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z' />
                  <polyline points='22,6 12,13 2,6' />
                </svg>
              </div>
              <h1 className='unsubscribe-title'>Manage email preferences</h1>
              <p className='unsubscribe-subtitle'>
                Choose the email categories you'd like to unsubscribe from.
              </p>
            </div>

            <div className='unsubscribe-categories'>
              {CATEGORIES.map((cat) => (
                <label
                  key={cat.id}
                  className={`unsubscribe-category-card${selected.has(cat.id) ? ' is-selected' : ''}`}
                  htmlFor={`cat-${cat.id}`}
                >
                  <div className='unsubscribe-category-icon'>{cat.icon}</div>
                  <div className='unsubscribe-category-body'>
                    <div className='unsubscribe-category-top'>
                      <span className='unsubscribe-category-label'>{cat.label}</span>
                    </div>
                    <p className='unsubscribe-category-desc'>{cat.description}</p>
                  </div>
                  <input
                    id={`cat-${cat.id}`}
                    type='checkbox'
                    className='unsubscribe-checkbox'
                    checked={selected.has(cat.id)}
                    onChange={() => toggleCategory(cat.id)}
                  />
                </label>
              ))}
            </div>

            <div className='unsubscribe-notice'>
              <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' width='16' height='16'>
                <circle cx='12' cy='12' r='10' />
                <line x1='12' y1='8' x2='12' y2='12' />
                <line x1='12' y1='16' x2='12.01' y2='16' />
              </svg>
              <span>
                <strong>Note:</strong> Mandatory transactional emails (such as payment receipts, security alerts, and password resets) will still be sent regardless of your preferences.
              </span>
            </div>

            <button
              className='unsubscribe-btn-primary'
              disabled={noneSelected}
              onClick={handleSubmit}
              id='unsubscribe-submit-btn'
            >
              Unsubscribe
            </button>
          </>
        )}

        {/* ─── LOADING STATE ─── */}
        {screen === 'loading' && (
          <div className='unsubscribe-center'>
            <div className='unsubscribe-spinner' />
            <p className='unsubscribe-loading-text'>Updating your preferences…</p>
          </div>
        )}

        {/* ─── SUCCESS STATE ─── */}
        {screen === 'success' && (
          <div className='unsubscribe-terminal'>
            <div className='unsubscribe-icon-wrap unsubscribe-icon-success'>✓</div>
            <h2 className='unsubscribe-title'>Preferences saved</h2>
            <p className='unsubscribe-subtitle'>
              You have been unsubscribed from the selected email categories.
            </p>
            <p className='unsubscribe-subtitle' style={{ marginTop: 8 }}>
              Note: Mandatory transactional emails (receipts, password resets, security alerts) will continue to
              be sent.
            </p>
          </div>
        )}

        {/* ─── ERROR STATE ─── */}
        {screen === 'error' && (
          <div className='unsubscribe-terminal'>
            <div className='unsubscribe-icon-wrap unsubscribe-icon-error'>✕</div>
            <h2 className='unsubscribe-title'>Something went wrong</h2>
            <p className='unsubscribe-subtitle unsubscribe-error-text'>{errorMessage}</p>
            <button
              className='unsubscribe-btn-secondary'
              onClick={() => setScreen('selecting')}
              id='unsubscribe-retry-btn'
            >
              Try again
            </button>
          </div>
        )}

        {/* ─── INVALID TOKEN STATE ─── */}
        {screen === 'invalid_token' && (
          <div className='unsubscribe-terminal'>
            <div className='unsubscribe-icon-wrap unsubscribe-icon-error'>✕</div>
            <h2 className='unsubscribe-title'>Invalid link</h2>
            <p className='unsubscribe-subtitle'>
              This unsubscribe link is invalid or has expired. Please use the unsubscribe link from a recent
              email.
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default UnsubscribeEmails;
